import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import { useFetcher, useNavigate, useRouteLoaderData } from '@remix-run/react';
import { useSnapshot } from 'valtio';
import { TentTypeV1, TENT_TYPE_V1_TO_NIVAAS_SLUG } from '@camp67/model';
import { useIntercom } from 'react-use-intercom';
import { phoneCodes } from '@kumbh/booking-components';
import phoneValidator from 'phone';

import { useDisableBodyScroll } from '../../hooks/use-disable-body-scroll';
import { inquiryModal, onClose } from '../../state/inquiry-modal';
import { useMessages } from '../../i18n/useMessages';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '../ui/form';
import { testId } from '../ui/test-id';
import {
  inquirySchema,
  InquiryFormValues,
  tentOptions,
  calculateCapacity,
  calculateTotalRooms,
} from './inquiry-schema';
import type { loader as rootLoader } from '../../root';
import { NivaasOffering, getOfferingFullRoomPrice } from '../../types/offerings';
import { displayPrice } from '../../state/prices';
import { environmentState } from '../../state/environment';

// Get default country code based on detected country
const getDefaultCountryCode = () => {
  const countryCode = environmentState.countryCode || 'IN';
  const country = phoneCodes.find((c) => c.c === countryCode);
  return country?.d || '+91';
};

export const EnhancedInquiryModal: React.FC = () => {
  const { isOpen, preferences } = useSnapshot(inquiryModal);
  useDisableBodyScroll(isOpen);
  const { update: updateIntercom } = useIntercom();
  const [countryCode, setCountryCode] = useState(getDefaultCountryCode);
  const [isCountryDropdownVerbose, setIsCountryDropdownVerbose] = useState(false);

  // Hide Intercom launcher when modal is open
  useEffect(() => {
    updateIntercom({ hideDefaultLauncher: isOpen });
  }, [isOpen, updateIntercom]);

  const fetcher = useFetcher();
  const getMessage = useMessages();
  const navigate = useNavigate();

  // Get offerings data from root loader
  const rootData = useRouteLoaderData<typeof rootLoader>('root');
  const offerings = useMemo(
    () => (rootData?.offerings || []) as NivaasOffering[],
    [rootData?.offerings],
  );

  // Create a map of tent type -> price for quick lookup (full room price)
  const offeringPrices = useMemo(() => {
    const priceMap: Record<string, string | null> = {};
    for (const option of tentOptions) {
      const slug = TENT_TYPE_V1_TO_NIVAAS_SLUG[option.value];
      const offering = offerings.find((o) => o.slug === slug);
      const price = getOfferingFullRoomPrice(offering);
      priceMap[option.value] = price ? displayPrice({ priceInINR: price.amount }) : null;
    }
    return priceMap;
  }, [offerings]);

  const form = useForm<InquiryFormValues>({
    resolver: zodResolver(inquirySchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      tents: {},
      checkIn: '',
      checkOut: '',
      guests: '',
      message: '',
    },
  });

  const tents = form.watch('tents');
  const guests = form.watch('guests');

  // Calculate capacities
  const totalCapacity = useMemo(() => calculateCapacity(tents), [tents]);
  const totalRooms = useMemo(() => calculateTotalRooms(tents), [tents]);

  // Calculate estimated nightly price from offerings (full room prices)
  const estimatedNightlyPrice = useMemo(() => {
    let total = 0;
    for (const option of tentOptions) {
      const count = tents[option.value] ?? 0;
      if (count > 0) {
        const slug = TENT_TYPE_V1_TO_NIVAAS_SLUG[option.value];
        const offering = offerings.find((o) => o.slug === slug);
        const price = getOfferingFullRoomPrice(offering);
        if (price) {
          total += price.amount * count;
        }
      }
    }
    return total;
  }, [tents, offerings]);

  const guestCount = parseInt(guests || '0') || 0;
  // Only show over-capacity warning when accommodation is selected
  const isOverCapacity = totalRooms > 0 && guestCount > totalCapacity;

  // Pre-fill from preferences
  useEffect(() => {
    if (preferences) {
      if (preferences.tentType) {
        form.setValue('tents', { [preferences.tentType as TentTypeV1]: 1 });
      }
      if (preferences.checkIn) form.setValue('checkIn', preferences.checkIn);
      if (preferences.checkOut) form.setValue('checkOut', preferences.checkOut);
      if (preferences.guests) form.setValue('guests', String(preferences.guests));
    }
  }, [preferences, form]);

  // Cap guest count at capacity when accommodation is selected
  useEffect(() => {
    // Only cap guests if accommodation is selected AND guests exceed capacity
    if (totalRooms > 0 && guestCount > totalCapacity) {
      form.setValue('guests', String(totalCapacity));
    }
    // When accommodation is removed, keep the current guest count (don't reset)
  }, [totalCapacity, totalRooms, guestCount, form]);

  // Update tent quantity
  const updateTentQuantity = useCallback(
    (tentType: TentTypeV1, delta: number) => {
      const currentTents = form.getValues('tents');
      const currentCount = currentTents[tentType] ?? 0;
      const newCount = Math.max(0, Math.min(10, currentCount + delta));

      if (newCount === 0) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [tentType]: _removed, ...rest } = currentTents;
        form.setValue('tents', rest);
      } else {
        const newTents = { ...currentTents, [tentType]: newCount };
        form.setValue('tents', newTents);

        // Auto-fill guests with total capacity when adding rooms
        if (delta > 0) {
          const newCapacity = calculateCapacity(newTents);
          form.setValue('guests', String(newCapacity));
        }
      }
    },
    [form],
  );

  // Handle success - redirect to confirmation page
  useEffect(() => {
    const data = fetcher.data as { success: boolean; inquiryId?: string } | undefined;
    if (data?.success && data?.inquiryId) {
      onClose();
      navigate(`/inquiry/${btoa(data.inquiryId)}`);
    }
  }, [fetcher.data, navigate]);

  const onSubmit = useCallback(
    (values: InquiryFormValues) => {
      if (isOverCapacity) return;

      // Combine country code with phone number
      const fullPhone = values.phone.startsWith('+')
        ? values.phone
        : `${countryCode}${values.phone.replace(/^0+/, '')}`;

      // Validate combined phone number
      if (!phoneValidator(fullPhone).isValid) {
        form.setError('phone', {
          type: 'manual',
          message: 'Please enter a valid phone number',
        });
        return;
      }

      // Convert tents to array format for backend
      const tentTypesArray = Object.entries(values.tents)
        .filter(([, count]) => (count ?? 0) > 0)
        .flatMap(([type, count]) => Array(count).fill(type));

      // Send structured data
      fetcher.submit(
        {
          name: values.name,
          email: values.email,
          phone: fullPhone,
          tentTypes: JSON.stringify(tentTypesArray),
          checkIn: values.checkIn || '',
          checkOut: values.checkOut || '',
          guests: values.guests || '',
          message: values.message || '',
        },
        { method: 'post', action: '/?index' },
      );
    },
    [fetcher, isOverCapacity, countryCode, form],
  );

  const onReset = useCallback(() => {
    form.reset();
  }, [form]);

  const handleBackdropClick = useCallback(() => {
    onClose();
  }, []);

  const handleBackdropKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, []);

  if (!isOpen) return null;

  const isSubmitting = fetcher.state === 'submitting';

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4">
      {/* Backdrop */}
      <div
        role="button"
        tabIndex={0}
        aria-label={getMessage('camp67-inquiry.close')}
        {...testId('inquiry-backdrop')}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm cursor-pointer"
        onClick={handleBackdropClick}
        onKeyDown={handleBackdropKeyDown}
      />

      {/* Modal */}
      <div
        {...testId('inquiry-modal')}
        className="relative w-full sm:max-w-2xl max-h-[95dvh] sm:max-h-[90vh] overflow-y-auto overflow-x-hidden bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl animate-in fade-in slide-in-from-bottom-4 sm:zoom-in-95 duration-200"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-red-200 px-4 sm:px-6 py-4 sm:py-5 rounded-t-2xl">
          <button
            type="button"
            onClick={onClose}
            {...testId('inquiry-close-button')}
            aria-label="Close"
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors"
          >
            <svg
              className="w-5 h-5 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
          <h2 className="text-xl sm:text-2xl font-serif font-bold text-white pr-8">
            {getMessage('camp67-inquiry.modal-title')}
          </h2>
          <p className="text-orange-100 mt-1 text-sm sm:text-base">
            {getMessage('camp67-inquiry.modal-subtitle')}
          </p>
        </div>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="mx-4 py-4 sm:p-6 space-y-5 sm:space-y-6"
          >
            {/* Accommodation Selection */}
            <div className="space-y-3 sm:space-y-4">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-wider">
                  {getMessage('camp67-inquiry.accommodation-preference')}
                </h3>
                {totalRooms > 0 && (
                  <span className="text-xs text-orange-600 font-medium bg-orange-50 px-2 py-1 rounded-full whitespace-nowrap">
                    {totalRooms} {totalRooms === 1 ? 'room' : 'rooms'}
                  </span>
                )}
              </div>

              <div className="space-y-2">
                {tentOptions.map((option) => {
                  const count = tents[option.value] ?? 0;
                  const isSelected = count > 0;
                  return (
                    <div
                      key={option.value}
                      className={clsx(
                        'flex items-center justify-between p-3 sm:p-4 rounded-xl border-2 transition-all gap-2',
                        isSelected
                          ? 'bg-orange-50 border-orange-300'
                          : 'bg-gray-50 border-transparent hover:border-gray-200',
                      )}
                      {...testId(`inquiry-tent-${option.value}`)}
                    >
                      <div className="flex-1 min-w-0">
                        <span
                          className={clsx(
                            'font-semibold block text-sm sm:text-base truncate',
                            isSelected ? 'text-orange-800' : 'text-gray-800',
                          )}
                        >
                          {getMessage(option.labelKey)}
                        </span>
                        <div className="flex flex-wrap items-center gap-2 mt-1">
                          <span className="text-xs text-gray-400 flex items-center gap-1">
                            <svg
                              className="w-3 h-3 flex-shrink-0"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                              />
                            </svg>
                            {option.capacity} {option.capacity === 1 ? 'guest' : 'guests'}
                          </span>
                          {offeringPrices[option.value] && (
                            <>
                              <span className="text-gray-300">•</span>
                              <span className="text-xs font-semibold text-orange-600">
                                {offeringPrices[option.value]}/night
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
                        <button
                          {...testId(`inquiry-tent-${option.value}-decrement`)}
                          type="button"
                          onClick={() => updateTentQuantity(option.value, -1)}
                          disabled={count === 0}
                          className={clsx(
                            'w-9 h-9 sm:w-8 sm:h-8 rounded-full flex items-center justify-center transition-all text-lg font-medium',
                            count === 0
                              ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                              : 'bg-orange-100 text-orange-600 hover:bg-orange-200 active:bg-orange-300',
                          )}
                        >
                          −
                        </button>
                        <span
                          className={clsx(
                            'w-6 sm:w-8 text-center font-semibold text-sm sm:text-base',
                            isSelected ? 'text-orange-700' : 'text-gray-400',
                          )}
                        >
                          {count}
                        </span>
                        <button
                          {...testId(`inquiry-tent-${option.value}-increment`)}
                          type="button"
                          onClick={() => updateTentQuantity(option.value, 1)}
                          className="w-9 h-9 sm:w-8 sm:h-8 rounded-full bg-orange-500 text-white flex items-center justify-center hover:bg-orange-600 active:bg-orange-700 transition-all text-lg font-medium"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Capacity and Price Info */}
              {totalRooms > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
                  <p className="text-sm text-amber-700">
                    Your selection fits up to {totalCapacity} guests
                  </p>
                  {estimatedNightlyPrice > 0 && (
                    <p className="text-sm font-semibold text-amber-800">
                      Estimated: {displayPrice({ priceInINR: estimatedNightlyPrice })}
                      /night
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Dates Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <FormField
                control={form.control}
                name="checkIn"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs sm:text-sm">
                      {getMessage('camp67-inquiry.checkin-date')}
                    </FormLabel>
                    <FormControl>
                      <input
                        {...field}
                        id="inquiry-checkin"
                        type="date"
                        min="2025-12-31"
                        max="2026-02-20"
                        className="w-full max-w-full box-border px-3 py-3 sm:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-base"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="checkOut"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs sm:text-sm">
                      {getMessage('camp67-inquiry.checkout-date')}
                    </FormLabel>
                    <FormControl>
                      <input
                        {...field}
                        id="inquiry-checkout"
                        type="date"
                        min={form.watch('checkIn') || '2025-12-31'}
                        max="2026-02-21"
                        className="w-full max-w-full box-border px-3 py-3 sm:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-base"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Guests */}
            <FormField
              control={form.control}
              name="guests"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs sm:text-sm">
                    {getMessage('camp67-inquiry.number-of-guests')}
                  </FormLabel>
                  <FormControl>
                    <input
                      {...field}
                      id="inquiry-guests"
                      type="number"
                      min="1"
                      max={totalRooms > 0 ? totalCapacity : undefined}
                      placeholder={getMessage('camp67-inquiry.how-many')}
                      className={clsx(
                        'w-full px-3 py-3 sm:py-2.5 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-base',
                        isOverCapacity ? 'border-red-400 bg-red-50' : 'border-gray-300',
                      )}
                    />
                  </FormControl>
                  {isOverCapacity && (
                    <p className="text-xs text-red-600">
                      Maximum capacity is {totalCapacity} guests. Add more rooms or reduce
                      guest count.
                    </p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <hr className="border-gray-200" />

            {/* Contact Information */}
            <div className="space-y-3 sm:space-y-4">
              <h3 className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-wider">
                {getMessage('camp67-inquiry.contact-details')}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs sm:text-sm">
                        {getMessage('camp67-inquiry.full-name')} *
                      </FormLabel>
                      <FormControl>
                        <input
                          {...field}
                          id="inquiry-name"
                          type="text"
                          placeholder={getMessage('camp67-inquiry.your-name')}
                          className={clsx(
                            'w-full px-3 py-3 sm:py-2.5 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-base',
                            form.formState.errors.name
                              ? 'border-red-400'
                              : 'border-gray-300',
                          )}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs sm:text-sm">
                        {getMessage('camp67-inquiry.email')} *
                      </FormLabel>
                      <FormControl>
                        <input
                          {...field}
                          id="inquiry-email"
                          type="email"
                          placeholder={getMessage('camp67-inquiry.email-placeholder')}
                          className={clsx(
                            'w-full px-3 py-3 sm:py-2.5 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-base',
                            form.formState.errors.email
                              ? 'border-red-400'
                              : 'border-gray-300',
                          )}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs sm:text-sm">
                      {getMessage('camp67-inquiry.phone')} (with country code) *
                    </FormLabel>
                    <FormControl>
                      <div
                        className={clsx(
                          'flex items-center border rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-orange-500',
                          form.formState.errors.phone
                            ? 'border-red-400'
                            : 'border-gray-300',
                        )}
                      >
                        <select
                          value={countryCode}
                          onChange={(e) => {
                            setCountryCode(e.target.value);
                            setIsCountryDropdownVerbose(false);
                          }}
                          onPointerDownCapture={() => setIsCountryDropdownVerbose(true)}
                          onBlurCapture={() => setIsCountryDropdownVerbose(false)}
                          className="h-full px-2 py-3 sm:py-2.5 bg-gray-50 border-r border-gray-300 text-sm focus:outline-none"
                        >
                          {phoneCodes.map((country) => (
                            <option key={country.c} value={country.d}>
                              {isCountryDropdownVerbose
                                ? `${country.e} ${country.n}`
                                : `${country.e} ${country.d}`}
                            </option>
                          ))}
                        </select>
                        <input
                          {...field}
                          id="inquiry-phone"
                          type="tel"
                          placeholder={getMessage('camp67-inquiry.phone-placeholder')}
                          className="flex-1 px-3 py-3 sm:py-2.5 text-base focus:outline-none"
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Additional Message */}
            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs sm:text-sm">
                    {getMessage('camp67-inquiry.additional-message')}
                  </FormLabel>
                  <FormControl>
                    <textarea
                      {...field}
                      id="inquiry-message"
                      placeholder={getMessage('camp67-inquiry.message-placeholder')}
                      rows={3}
                      className="w-full px-3 py-3 sm:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none text-base"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Actions */}
            <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2 pb-2 sm:pb-0">
              <button
                type="button"
                onClick={onReset}
                className="py-3 sm:py-3.5 px-6 rounded-xl font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 transition-colors"
              >
                {getMessage('camp67.reset')}
              </button>
              <button
                type="submit"
                disabled={isSubmitting || isOverCapacity}
                className={clsx(
                  'flex-1 py-3.5 sm:py-3.5 px-6 rounded-xl font-semibold text-white transition-all flex items-center justify-center gap-2',
                  isSubmitting || isOverCapacity
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-red-200 hover:bg-red-300 active:bg-red-400 shadow-lg shadow-red-400/30 hover:shadow-xl',
                )}
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    {getMessage('camp67-inquiry.submitting')}
                  </>
                ) : (
                  getMessage('camp67-inquiry.submit-interest')
                )}
              </button>
            </div>

            <p className="text-xs text-gray-500 text-center pb-safe">
              {getMessage('camp67-inquiry.secure-info')}
            </p>
          </form>
        </Form>
      </div>
    </div>
  );
};
